import { FC, useState, useEffect } from "react";
import { 
  Box, 
  TextField, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Paper,
  CircularProgress,
  Alert
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Connection, FileServerType, FileServerCredentials, FileServerPath } from "types";
import type { SelectChangeEvent } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { listFileServer } from "api/callbacks/fileserver";

// FileServerFormData extends FileServerCredentials to allow for future expansion
interface FileServerFormData {
  credentials: FileServerCredentials;
  identifier: string;
  format: string;
  paths?: FileServerPath[];
  fileNameRegex?: string;
  isValid?: boolean; // Add validity flag to communicate to parent
}

interface Props {
  onChange: (value: FileServerFormData) => void;
  initialData?: Connection;
}

const FileServerConnectionForm: FC<Props> = ({ onChange, initialData }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FileServerFormData>({
    credentials: {
      type: FileServerType.SFTP,
      host: "",
      username: "",
      password: ""
    },
    identifier: "",
    format: "TSV", // Default format
    paths: [],
    fileNameRegex: "",
    isValid: false
  });
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [isListingFiles, setIsListingFiles] = useState<boolean>(false);
  const [availablePaths, setAvailablePaths] = useState<FileServerPath[]>([]);
  const [formatDropdownOpen, setFormatDropdownOpen] = useState<boolean>(false);

  const checkFormValidity = (data: FileServerFormData): boolean => {
    return Boolean(
      data.credentials.host && 
      data.credentials.username && 
      data.credentials.password && 
      data.identifier && 
      data.paths && 
      data.paths.length > 0
    );
  };

  useEffect(() => {
    setFormData(prevData => {
      const isValid = checkFormValidity(prevData);
      const updatedData = { ...prevData, isValid };
      onChange(updatedData);
      return updatedData;
    });
  }, [
    formData.credentials.host, 
    formData.credentials.username, 
    formData.credentials.password,
    formData.identifier,
    formData.paths,
    formData.fileNameRegex,
    onChange
  ]);

  useEffect(() => {
    if (initialData?.metadata) {
      const metadata = initialData.metadata;
      
      let paths: FileServerPath[] = [];
      if (metadata.paths) {
        console.log('metadata.paths',metadata.paths);
        if (Array.isArray(metadata.paths)) {
          paths = metadata.paths;
        } else {
          paths = JSON.parse(metadata.paths);
        }
      }
      
      const newFormData = {
        credentials: {
          type: (typeof metadata.type === 'string' ? 
            parseInt(metadata.type, 10) as FileServerType : 
            metadata.type as FileServerType) || FileServerType.SFTP,
          host: metadata.host || "",
          username: metadata.username || "",
          password: metadata.password || ""
        },
        identifier: metadata.identifier || "",
        format: metadata.format || "TSV",
        paths,
        fileNameRegex: metadata.fileNameRegex || "",
      };
      
      const isValid = checkFormValidity(newFormData);
      setFormData({ ...newFormData, isValid });
    }
  }, [initialData]);

  const handleCredentialChange = (field: keyof FileServerCredentials, value: string | FileServerType) => {
    const newCredentials = { ...formData.credentials, [field]: value };
    const newFormData = { ...formData, credentials: newCredentials };
    setFormData(newFormData);
    onChange(newFormData);
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    handleCredentialChange('type', parseInt(event.target.value, 10) as FileServerType);
  };

  const handleListFiles = async () => {
    try {
      setIsListingFiles(true);
      const response = await listFileServer(formData.credentials);
      const files = response.data;
      setAvailablePaths(files);
    } catch (error) {
      console.error("Error listing files:", error);
    } finally {
      setIsListingFiles(false);
    }
  };

  const handlePathToggle = (path: FileServerPath) => () => {
    const currentPaths = formData.paths || [];
    let newPaths: FileServerPath[];

    // Check if this path is already selected
    const existingIndex = currentPaths.findIndex(p => p.path === path.path);
    
    if (existingIndex !== -1) {
      // Remove from selection
      newPaths = [
        ...currentPaths.slice(0, existingIndex),
        ...currentPaths.slice(existingIndex + 1)
      ];
    } else {
      // Add to selection
      newPaths = [...currentPaths, path];
    }

    const newFormData = {
      ...formData,
      paths: newPaths
    };
    setFormData(newFormData);
    onChange(newFormData);
  };

  const isPathSelected = (path: string): boolean => {
    return (formData.paths || []).some(p => p.path === path);
  };

  const handleIdentifierChange = (value: string) => {
    const newFormData = { ...formData, identifier: value };
    setFormData(newFormData);
    onChange(newFormData);
  };

  const handleFormatChange = (event: SelectChangeEvent) => {
    const newFormData = { ...formData, format: event.target.value };
    setFormData(newFormData);
    onChange(newFormData);
  };

  const handleFileNameRegexChange = (value: string) => {
    const newFormData = { ...formData, fileNameRegex: value };
    setFormData(newFormData);
    onChange(newFormData);
  };

  return (
    <Box>
      {/* Lake settings group */}
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t("connection:lake_settings")}
        </Typography>
        
        <TextField
          label={t("connection:identifier")}
          value={formData.identifier}
          onChange={(e) => handleIdentifierChange(e.target.value)}
          fullWidth
          variant="standard"
          helperText={t("connection:identifier_help")}
          sx={{ mb: 3 }}
          disabled={!!initialData}
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ ml: -3.5 }}>{t("connection:file_format")}</InputLabel>
          <Select
            IconComponent={() =>
              formatDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
            }
            value={formData.format}
            onChange={handleFormatChange}
            variant="standard"
            sx={{ color: "black", width: "100%" }}
            onOpen={() => setFormatDropdownOpen(true)}
            onClose={() => setFormatDropdownOpen(false)}
            disabled={!!initialData}
          >
            <MenuItem value="TSV">TSV</MenuItem>
          </Select>
        </FormControl>

        {/* Lake destination preview */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            fontStyle: 'italic',
            bgcolor: '#f5f5f5',
            p: 1,
            borderRadius: 1
          }}
        >
          {t("connection:lake_destination_preview")}: <Box component="span" sx={{ fontWeight: 'bold' }}>
            Files/Raw/FileServer_{formData.format}/{formData.identifier || "___"}
          </Box>
        </Typography>
      </Paper>

      {/* Server connection group */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t("connection:server_connection")}
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ ml: -3.5 }}>{t("connection:file_server_type")}</InputLabel>
          <Select
            IconComponent={() =>
              dropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
            }
            value={formData.credentials.type.toString()}
            onChange={handleTypeChange}
            disabled={true} // Disable for now since we only support SFTP
            variant="standard"
            sx={{ color: "black", width: "100%" }}
            onOpen={() => setDropdownOpen(true)}
            onClose={() => setDropdownOpen(false)}
          >
            <MenuItem value={FileServerType.SFTP.toString()}>SFTP</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label={t("connection:server_address")}
          value={formData.credentials.host}
          onChange={(e) => handleCredentialChange('host', e.target.value)}
          fullWidth
          variant="standard"
          helperText={t("connection:server_address_help")}
          sx={{ mb: 3 }}
          disabled={!!initialData}
        />

        <TextField
          label={t("connection:username")}
          value={formData.credentials.username}
          onChange={(e) => handleCredentialChange('username', e.target.value)}
          fullWidth
          variant="standard"
          sx={{ mb: 3 }}
          disabled={!!initialData}
        />

        <TextField
          label={t("connection:password")}
          value={formData.credentials.password}
          onChange={(e) => handleCredentialChange('password', e.target.value)}
          fullWidth
          variant="standard"
          type="password"
          sx={{ mb: 3 }}
          disabled={!!initialData}
        />
      </Paper>

      {/* The rest of the form (List Files button and file selection) */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<FolderOpenIcon />}
        onClick={handleListFiles}
        disabled={isListingFiles || !formData.credentials.host || !formData.credentials.username || !formData.credentials.password}
        sx={{ mb: 2 }}
      >
        {isListingFiles ? t("common:loading") : t("connection:list_files")}
      </Button>

      {isListingFiles && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {availablePaths.length > 0 && (
        <Box mt={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("connection:file_server_selection_info")}
          </Alert>
          
          <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
            <List dense disablePadding>
              {availablePaths.map((path, index) => (
                <ListItem 
                  key={index}
                  onClick={handlePathToggle(path)}
                  dense
                  disableGutters
                  sx={{ 
                    cursor: 'pointer',
                    py: 0.25,
                    px: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      edge="start"
                      checked={isPathSelected(path.path)}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {path.isFolder ? 
                      <FolderIcon color="primary" fontSize="small" /> : 
                      <InsertDriveFileIcon fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary={path.path} 
                    primaryTypographyProps={{
                      style: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {(formData.paths || []).length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            {t("connection:selected_items")} ({formData.paths?.length})
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, background: '#f5f5f5' }}>
            <List dense disablePadding>
              {formData.paths?.map((path, index) => (
                <ListItem 
                  key={index} 
                  dense 
                  disableGutters
                  sx={{ 
                    py: 0.25,
                    px: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {path.isFolder ? 
                      <FolderIcon fontSize="small" color="primary" /> : 
                      <InsertDriveFileIcon fontSize="small" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary={path.path}
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      style: { 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>
          {t("connection:filename_regex")}
        </Typography>
        <TextField
          label={t("connection:filename_regex")}
          value={formData.fileNameRegex}
          onChange={(e) => handleFileNameRegexChange(e.target.value)}
          fullWidth
          variant="outlined"
          helperText={t("connection:filename_regex_help")}
          sx={{ mb: 2 }}
          disabled={!!initialData}
        />
      </Box>
    </Box>
  );
};

export default FileServerConnectionForm; 